/**
 * Validates a name with support for international characters, accents, and common name variations.
 * 
 * Allowed characters:
 * - Unicode letters (a-z, A-Z, accented characters, non-Latin scripts, etc.)
 * - Spaces
 * - Hyphens (for names like Mary-Jane)
 * - Apostrophes (for names like O'Brien)
 * 
 * Rules:
 * - Must not be empty
 * - Must start with a letter (not a number or space)
 * - Must be between 2 and 100 characters
 * 
 * @param value - The name to validate
 * @returns Error message if invalid, empty string if valid
 */
export const validateInternationalName = (value: string): string => {
  if (!value.trim()) {
    return "Name is required.";
  }

  const trimmedValue = value.trim();

  // Check minimum length (at least 2 characters)
  if (trimmedValue.length < 2) {
    return "Name must be at least 2 characters long.";
  }

  // Check maximum length
  if (trimmedValue.length > 100) {
    return "Name must not exceed 100 characters.";
  }

  // Check if starts with a letter (including accented/international characters)
  // Checks if first character is NOT a digit or space
  const firstChar = trimmedValue.charAt(0);
  if (/\d/.test(firstChar) || /\s/.test(firstChar)) {
    return "Name must start with a letter.";
  }

  // Check that name contains only allowed characters
  // This regex allows:
  // - Basic ASCII letters (a-z, A-Z) and digits via \w
  // - Latin Extended-A/B (À-ÿ and beyond) via Unicode escape ranges
  // - Spaces, hyphens, apostrophes
  // ES5 compatible version using explicit Unicode ranges
  // \u00C0-\u00FF = Latin Extended-A
  // \u0100-\u017F = Latin Extended-B
  // \u0180-\u024F = Latin Extended Additional
  if (!/^[\w\u00C0-\u00FF\u0100-\u017F\u0180-\u024F\s\-']+$/i.test(trimmedValue)) {
    return "Name can only contain letters, numbers, spaces, hyphens, and apostrophes.";
  }

  // Check for excessive spaces or special characters in a row
  if (/\s{2,}/.test(trimmedValue)) {
    return "Name cannot contain multiple spaces in a row.";
  }

  if (/[-']{2,}/.test(trimmedValue)) {
    return "Name cannot contain multiple hyphens or apostrophes in a row.";
  }

  return "";
};
